import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { retryFailedCall } from './callRetry'
import { hasInternalApi, internalApiGet, internalApiPatch } from './internalApi'
import { supabase } from './supabase'
import type { EditableTodo } from './todoEditing'

export type CallStatus =
  | 'pending'
  | 'recording'
  | 'transcribing'
  | 'awaiting_summary'
  | 'summarizing'
  | 'complete'
  | 'failed'

export interface CallRecord {
  id: string
  liver_id: string
  manager_id: string
  called_at: string
  duration_seconds: number | null
  speakers_detected: number | null
  transcript: string | null
  summary: string | null
  todos: EditableTodo[]
  next_follow_date: string | null
  status: CallStatus
  error_message: string | null
  audio_storage_path: string | null
  created_at: string
  updated_at: string
}

type CallsResponse = {
  calls: CallRecord[]
}

type CallResponse = {
  call: CallRecord
}

/**
 * Fetches call_records for a given liver, newest first.
 * Also subscribes to Supabase Realtime for INSERT/UPDATE events on this
 * liver's calls so the UI live-updates while the gateway processes them.
 */
export function useLiverCalls(liverId: string | undefined) {
  const qc = useQueryClient()

  const query = useQuery<CallRecord[]>({
    queryKey: ['calls', 'by-liver', liverId],
    enabled: !!liverId,
    queryFn: async () => {
      if (!liverId) return []
      if (hasInternalApi()) {
        const data = await internalApiGet<CallsResponse>(
          `/api/calls?liver_id=${encodeURIComponent(liverId)}&limit=50`
        )
        return data.calls
      }

      const { data, error } = await supabase
        .from('call_records')
        .select('*')
        .eq('liver_id', liverId)
        .order('called_at', { ascending: false })
        .limit(50)
      if (error) throw error
      return (data ?? []) as CallRecord[]
    }
  })

  // Realtime: refresh on any change to this liver's call_records.
  useEffect(() => {
    if (!liverId) return
    const ch = supabase
      .channel(`calls:by-liver:${liverId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'call_records',
          filter: `liver_id=eq.${liverId}`
        },
        () => {
          qc.invalidateQueries({ queryKey: ['calls', 'by-liver', liverId] })
        }
      )
      .subscribe()
    return () => {
      void supabase.removeChannel(ch)
    }
  }, [liverId, qc])

  return query
}

export function useRetryFailedCall(liverId: string | undefined) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: retryFailedCall,
    onSuccess: () => {
      if (liverId) {
        qc.invalidateQueries({ queryKey: ['calls', 'by-liver', liverId] })
        return
      }
      qc.invalidateQueries({ queryKey: ['calls'] })
    }
  })
}

export function useUpdateCallTodos(liverId: string | undefined) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ callId, todos }: { callId: string; todos: EditableTodo[] }) => {
      if (hasInternalApi()) {
        await internalApiPatch<CallResponse>(`/api/calls/${callId}`, { todos })
        return
      }

      const { error } = await supabase
        .from('call_records')
        .update({ todos })
        .eq('id', callId)
      if (error) throw error
    },
    onSuccess: () => {
      if (liverId) {
        qc.invalidateQueries({ queryKey: ['calls', 'by-liver', liverId] })
      }
    }
  })
}

export function useUpdateCallNextFollow(liverId: string | undefined) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ callId, nextFollowDate }: { callId: string; nextFollowDate: string | null }) => {
      if (hasInternalApi()) {
        await internalApiPatch<CallResponse>(`/api/calls/${callId}`, {
          next_follow_date: nextFollowDate
        })
        return
      }

      const { error } = await supabase
        .from('call_records')
        .update({ next_follow_date: nextFollowDate })
        .eq('id', callId)
      if (error) throw error
    },
    onSuccess: () => {
      if (liverId) {
        qc.invalidateQueries({ queryKey: ['calls', 'by-liver', liverId] })
      }
    }
  })
}
